import React, { useState, useEffect } from 'react';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import ProformaInvoiceForm from '../components/ProformaInvoiceForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useCreateProformaInvoiceHandler } from '../dataHandlers/createProformaInvoiceHandler';
import { useCreateDraftProformaInvoiceHandler } from '../dataHandlers/createDraftProformaInvoiceHandler';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_PROFORMA_INVOICE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function CreateProformaInvoicePage() {
  const { proformaInvoices: pi } = useTranslatedMessages();
  useDocumentTitle(pi?.pages?.create?.document_title ?? 'Proforma Invoice Form');
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!hasEInvoicePermission(CREATE_PROFORMA_INVOICE)) {
      showActionDeniedSnackbar();
      navigate('/sales/proforma-invoices', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  const onInvoiceCreated = (payload) => {
    navigate('/sales/proforma-invoices');
  };

  const {
    onSubmit: onCreateProforma,
    error: createProformaError,
    isPending: isCreateProformaPending,
  } = useCreateProformaInvoiceHandler({ onSuccess: onInvoiceCreated });

  const {
    onSubmit: onCreateDraftProforma,
    error: createDraftProformaError,
    isPending: isCreateDraftProformaPending,
  } = useCreateDraftProformaInvoiceHandler({ onSuccess: onInvoiceCreated });

  // Reset isSubmitting when mutation completes
  useEffect(() => {
    if ((!isCreateProformaPending || !isCreateDraftProformaPending) && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isCreateProformaPending, isCreateDraftProformaPending, isSubmitting]);

  const document = location.state?.document;

  const onSaveForm = async (data, isDraft) => {
    setIsSubmitting(true);
    if (isDraft) {
      await onCreateDraftProforma(data);
    } else {
      await onCreateProforma(data);
    }
  };

  const onCancel = async () => {
    navigate(location.state?.from || '/sales/proforma-invoices');
  };

  return (
    <Stack direction="column" sx={{ mt: '-2rem' }}>
      <ProformaInvoiceForm
        initialData={document || {}}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={createProformaError || createDraftProformaError}
        isLoading={isCreateProformaPending || isCreateDraftProformaPending || isSubmitting}
      />
    </Stack>
  );
}
